import {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
} from "react";

export interface UploadState {
  isUploading: boolean;
  uploadingText: string;
}

interface UploadStateContextValue {
  uploadState: UploadState;
  setUploadState: (state: Partial<UploadState>) => void;
  resetUploadState: () => void;
}

const UploadStateContext = createContext<UploadStateContextValue | undefined>(
  undefined,
);

interface UploadStateProviderProps {
  children: ReactNode;
}

export const UploadStateProvider = ({ children }: UploadStateProviderProps) => {
  const [uploadState, setUploadStateInternal] = useState<UploadState>({
    isUploading: false,
    uploadingText: "",
  });

  const setUploadState = useCallback((newState: Partial<UploadState>) => {
    setUploadStateInternal((prevState) => ({
      ...prevState,
      ...newState,
    }));
  }, []);

  const resetUploadState = useCallback(() => {
    setUploadStateInternal({
      isUploading: false,
      uploadingText: "",
    });
  }, []);

  return (
    <UploadStateContext.Provider
      value={{ uploadState, setUploadState, resetUploadState }}
    >
      {children}
    </UploadStateContext.Provider>
  );
};

export const useUploadState = (): UploadStateContextValue => {
  const context = useContext(UploadStateContext);
  if (context === undefined) {
    throw new Error(
      "useUploadState must be used within an UploadStateProvider",
    );
  }
  return context;
};
